import { cn } from '@v2/utils/cn';

const FALLBACK = '/img/favicon.png';

function initials(name: string | null | undefined): string {
    const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const SIZE = {
    sm: { dim: 'size-7',  initials: 'text-[9px]', name: 'max-w-[6rem] text-[9px]',  sub: 'text-[8px]',  pad: 'p-0.5', radius: 'rounded-lg' },
    md: { dim: 'size-11', initials: 'text-xs',    name: 'max-w-[7rem] text-[10px]', sub: 'text-[9px]',  pad: 'p-1',   radius: 'rounded-xl' },
    lg: { dim: 'size-16', initials: 'text-sm',    name: 'max-w-[8rem] text-[11px]', sub: 'text-[10px]', pad: 'p-1.5', radius: 'rounded-xl' },
    xl: { dim: 'size-24', initials: 'text-xl',    name: 'max-w-[10rem] text-xs',    sub: 'text-[11px]', pad: 'p-2.5', radius: 'rounded-2xl' },
} as const;

type Props = {
    name?: string | null;
    logoUrl?: string | null;
    color?: string;
    size?: keyof typeof SIZE;
    subtitle?: string;
    align?: 'left' | 'center' | 'right';
    dark?: boolean;
};

const ALIGN = {
    left:   'items-start text-left',
    center: 'items-center text-center',
    right:  'items-end text-right',
} as const;

export default function BrandAvatar({
    name, logoUrl, color = '#6366f1', size = 'md', subtitle, align = 'center', dark = false,
}: Props) {
    const s = SIZE[size];
    const displayName = (name ?? '').trim() || '—';

    const boxClass = cn(
        s.dim, s.pad, s.radius,
        'shrink-0 shadow-md',
        dark ? 'ring-1 ring-white/10' : 'ring-1 ring-gray-200/80',
    );

    return (
        <div className={cn('flex min-w-0 max-w-[11rem] flex-col gap-1.5', ALIGN[align])}>
            {logoUrl ? (
                <img
                    src={logoUrl}
                    alt={displayName}
                    className={cn(boxClass, dark ? 'bg-zinc-800 object-contain' : 'bg-white object-contain')}
                    onError={(e) => { (e.target as HTMLImageElement).src = FALLBACK; }}
                />
            ) : (
                <span
                    className={cn(boxClass, 'flex items-center justify-center font-bold text-white', s.initials)}
                    style={{ backgroundColor: color }}
                >
                    {initials(displayName)}
                </span>
            )}
            <p className={cn(
                'line-clamp-2 font-semibold uppercase leading-tight',
                s.name,
                dark ? 'text-gray-300' : 'text-gray-800',
            )}>
                {displayName}
            </p>
            {subtitle && (
                <p className={cn('font-medium uppercase tracking-wide', s.sub, dark ? 'text-gray-500' : 'text-gray-400')}>
                    {subtitle}
                </p>
            )}
        </div>
    );
}
